// ─────────────────────────────────────────────────────────────────────────────
// body_justification.ts — Per-page writing-guide patterns
//
// "Justification" here = the underlying alignment grid the user writes on:
// ruled horizontals, dot lattice, square graph paper, etc. Crisper than
// body_graphics; drawn between graphics and ink.
//
// Pure registry — adding a new pattern is one entry in REGISTRY.  The
// renderer uses the same draw signature as other decoration types, so
// the page-render pipeline doesn't change when patterns are added.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';

export interface BodyJustification {
  id: string;
  label: string;
  draw(ctx: CanvasRenderingContext2D, layout: PageLayout): void;
}

/** Inner content box — what the user writes onto.  All justification
 *  patterns clip to this rect so they don't bleed under the page
 *  margins, where margin decorations and per-page chrome live. */
function contentBox(L: PageLayout): { x: number; y: number; w: number; h: number } {
  return {
    x: L.x + L.marginLeft,
    y: L.y + L.marginTop,
    w: L.width - L.marginLeft - L.marginRight,
    h: L.height - L.marginTop - L.marginBottom,
  };
}

// ── Built-in patterns ───────────────────────────────────────────────────────

const NONE: BodyJustification = {
  id: 'none',
  label: 'Plain',
  draw: () => { /* nothing */ },
};

const LINES: BodyJustification = {
  id: 'lines',
  label: 'Ruled lines',
  draw(ctx, L) {
    const { x, y, w, h } = contentBox(L);
    const spacing = 28;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.25)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    for (let ly = y + spacing; ly < y + h; ly += spacing) {
      ctx.moveTo(x, ly);
      ctx.lineTo(x + w, ly);
    }
    ctx.stroke();
    ctx.restore();
  },
};

const DOTS: BodyJustification = {
  id: 'dots',
  label: 'Dot grid',
  draw(ctx, L) {
    const { x, y, w, h } = contentBox(L);
    const spacing = 24;
    ctx.save();
    ctx.fillStyle = 'rgba(110, 80, 50, 0.32)';
    for (let dy = y + spacing; dy < y + h; dy += spacing) {
      for (let dx = x + spacing; dx < x + w; dx += spacing) {
        ctx.beginPath();
        ctx.arc(dx, dy, 0.9, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  },
};

const GRID: BodyJustification = {
  id: 'grid',
  label: 'Graph paper',
  draw(ctx, L) {
    const { x, y, w, h } = contentBox(L);
    const spacing = 20;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.20)';
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    for (let dx = x + spacing; dx < x + w; dx += spacing) {
      ctx.moveTo(dx, y);
      ctx.lineTo(dx, y + h);
    }
    for (let dy = y + spacing; dy < y + h; dy += spacing) {
      ctx.moveTo(x, dy);
      ctx.lineTo(x + w, dy);
    }
    ctx.stroke();
    ctx.restore();
  },
};

/** College-ruled — tighter horizontal lines, blue-grey tone. */
const COLLEGE: BodyJustification = {
  id: 'college',
  label: 'College ruled',
  draw(ctx, L) {
    const { x, y, w, h } = contentBox(L);
    const spacing = 22;
    ctx.save();
    ctx.strokeStyle = 'rgba(80, 100, 130, 0.22)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    for (let ly = y + spacing; ly < y + h; ly += spacing) {
      ctx.moveTo(x, ly);
      ctx.lineTo(x + w, ly);
    }
    ctx.stroke();
    ctx.restore();
  },
};

/** Wide-ruled — generous spacing for big handwriting. */
const WIDE: BodyJustification = {
  id: 'wide',
  label: 'Wide ruled',
  draw(ctx, L) {
    const { x, y, w, h } = contentBox(L);
    const spacing = 36;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.22)';
    ctx.lineWidth = 0.55;
    ctx.beginPath();
    for (let ly = y + spacing; ly < y + h; ly += spacing) {
      ctx.moveTo(x, ly);
      ctx.lineTo(x + w, ly);
    }
    ctx.stroke();
    ctx.restore();
  },
};

/** Cornell notes — vertical cue-column divider + bottom summary band. */
const CORNELL: BodyJustification = {
  id: 'cornell',
  label: 'Cornell notes',
  draw(ctx, L) {
    const { x, y, w, h } = contentBox(L);
    const cueX = x + Math.min(120, w * 0.22);   // cue column width
    const summaryY = y + h - Math.min(110, h * 0.18);   // summary band height
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.35)';
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    // Cue column divider — full height, dotted feel via dash
    ctx.setLineDash([4, 3]);
    ctx.moveTo(cueX, y);
    ctx.lineTo(cueX, summaryY);
    // Summary band top edge
    ctx.moveTo(x, summaryY);
    ctx.lineTo(x + w, summaryY);
    ctx.stroke();
    ctx.setLineDash([]);
    // Faint ruled lines inside the note-area
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.18)';
    ctx.lineWidth = 0.45;
    const spacing = 26;
    ctx.beginPath();
    for (let ly = y + spacing; ly < summaryY; ly += spacing) {
      ctx.moveTo(cueX + 6, ly);
      ctx.lineTo(x + w, ly);
    }
    ctx.stroke();
    ctx.restore();
  },
};

/** Isometric grid — three sets of lines at 0°, 60°, 120°. */
const ISOMETRIC: BodyJustification = {
  id: 'isometric',
  label: 'Isometric',
  draw(ctx, L) {
    const { x, y, w, h } = contentBox(L);
    const spacing = 22;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.18)';
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    // 60° lines (rising)
    const tan60 = Math.tan(Math.PI / 3);
    const xStride = spacing / Math.sin(Math.PI / 3);
    // Rising: dx positive, dy negative
    for (let i = -Math.ceil(h / xStride); i < (w + h * tan60) / xStride; i++) {
      const ax = x + i * xStride;
      const ay = y + h;
      const bx = ax + h / tan60;
      const by = y;
      ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
    }
    // Falling: dx positive, dy positive
    for (let i = -Math.ceil(h / xStride); i < (w + h * tan60) / xStride; i++) {
      const ax = x + i * xStride;
      const ay = y;
      const bx = ax + h / tan60;
      const by = y + h;
      ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
    }
    ctx.stroke();
    ctx.restore();
  },
};

// ── Registry ────────────────────────────────────────────────────────────────

const REGISTRY: Map<string, BodyJustification> = new Map();
function register(d: BodyJustification): void { REGISTRY.set(d.id, d); }

register(NONE);
register(LINES);
register(DOTS);
register(GRID);
register(COLLEGE);
register(WIDE);
register(CORNELL);
register(ISOMETRIC);

export function getBodyJustification(id: string | undefined | null): BodyJustification {
  if (!id) return NONE;
  return REGISTRY.get(id) ?? NONE;
}

export function listBodyJustifications(): BodyJustification[] {
  return [...REGISTRY.values()];
}
